import React, { useEffect, useMemo, useRef, useState } from "react";
import { LEARN } from "../../theme";
import { ConfettiBurst } from "../../feedback/Celebration";
import { clamp, seededShuffle, useCompleteOnce } from "./engineUtils";
import { Counter, ROOT_STYLE, SparkleBurst, StickerTile, tileSize, useArenaMetrics, useLetterAccent, useTileFeedback } from "./tapUtils";

/**
 * Letters hide across the scene as sticker tiles. Tap every target letter
 * (upper + lower case); found letters settle in place with a check.
 * Positions come from `layoutPoints` (normalised) or a seeded jittered grid.
 */
export default function LetterHuntGame({ letter, config = {}, speak, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { accent } = useLetterAccent(letter);
  const targets = useMemo(() => config.targets || [letter, String(letter).toLowerCase()], [config.targets, letter]);
  const targetSet = useMemo(() => new Set(targets.map(String)), [targets]);

  const tokens = useMemo(() => {
    let chars;
    if (config.tokenSequence?.length) {
      chars = config.tokenSequence.map(String);
    } else {
      const targetCopies = config.targetCopies || 6;
      const distractorCount = config.distractorCount || 12;
      const targetChars = Array.from({ length: targetCopies }, (_, i) => targets[i % targets.length]);
      const distractors = seededShuffle(config.distractors || [], 55).slice(0, distractorCount);
      chars = seededShuffle([...targetChars, ...distractors], 71).map(String);
    }
    const points = config.layoutPoints || [];
    const generated = points.length >= chars.length ? null : jitteredGrid(chars.length, 71);
    return chars.map((ch, i) => {
      const p = points[i] || generated?.[i] || [0.5, 0.5];
      const [nx, ny] = Array.isArray(p) ? p : [p.x, p.y];
      return {
        id: `t-${i}`,
        ch,
        isTarget: targetSet.has(ch),
        x: clamp(nx, 0.09, 0.91),
        y: clamp(ny, 0.1, 0.9),
        rot: ((i * 53) % 17) - 8,
      };
    });
  }, [config, targets, targetSet]);

  const targetTotal = tokens.filter((t) => t.isTarget).length;
  const [found, setFound] = useState(() => new Set());
  const rootRef = useRef(null);
  const { w, h, tile } = useArenaMetrics(rootRef);
  const { shakes, pulses, shakeId, pulseId } = useTileFeedback();
  const done = targetTotal > 0 && found.size >= targetTotal;

  useEffect(() => {
    if (!done) return undefined;
    sfx?.("sparkle");
    const t = setTimeout(finish, reduceMotion ? 250 : 900);
    return () => clearTimeout(t);
  }, [done, finish, reduceMotion, sfx]);

  const tap = (t) => {
    if (done || found.has(t.id)) return;
    if (!t.isTarget) { shakeId(t.id); onWrong?.(); return; }
    sfx?.("pop");
    speak?.(t.ch);
    pulseId(t.id);
    setFound((s) => new Set(s).add(t.id));
  };

  // Tiles size to the scatter density: the cell each token gets (arena /
  // grid) at ~78%, clamped so 18 tokens still fit a laptop arena and a phone.
  const { cols, rows } = gridShape(tokens.length);
  const mult = w && h && tile
    ? clamp((Math.min(w / cols, h / rows) * 0.78) / tile, 0.7, 1.05)
    : tokens.length > 12 ? 0.86 : 1;
  return (
    <div ref={rootRef} style={ROOT_STYLE}>
      <Counter done={found.size} total={targetTotal} accent={accent} suffix="found" />
      <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
        <ConfettiBurst active={done} originY={45} count={36} reduceMotion={reduceMotion} />
        {tokens.map((t, i) => {
          const isFound = found.has(t.id);
          return (
            <div
              key={t.id}
              style={{ position: "absolute", left: `${t.x * 100}%`, top: `${t.y * 100}%`, transform: "translate(-50%, -50%)", width: tileSize(mult), height: tileSize(mult) }}
            >
              <StickerTile
                size={mult}
                index={i}
                rotate={isFound ? 0 : t.rot}
                accent={accent}
                reduceMotion={reduceMotion}
                shake={shakes[t.id] || 0}
                pulse={pulses[t.id] || 0}
                state={isFound ? "done" : "idle"}
                disabled={isFound}
                ariaLabel={`Letter ${t.ch}`}
                onTap={() => tap(t)}
                style={isFound ? { background: `${accent}22`, opacity: 1, borderColor: `${accent}88` } : undefined}
              >
                <span style={{ fontSize: `calc(var(--tile, 72px) * ${mult} * 0.56)`, fontWeight: 700, lineHeight: 1, color: isFound ? accent : LEARN.color.ink }}>{t.ch}</span>
              </StickerTile>
              <SparkleBurst trigger={pulses[t.id] || 0} color={accent} reduceMotion={reduceMotion} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function gridShape(n) {
  const cols = n <= 8 ? 3 : n <= 12 ? 4 : 5;
  return { cols, rows: Math.ceil(n / cols) };
}

/** Deterministic scattered positions for n tokens (normalised 0–1). */
function jitteredGrid(n, seed) {
  const { cols, rows } = gridShape(n);
  const cells = Array.from({ length: cols * rows }, (_, i) => i);
  const order = seededShuffle(cells, seed).slice(0, n);
  return order.map((cell, i) => {
    const c = cell % cols;
    const r = Math.floor(cell / cols);
    const jx = (((i * 31 + seed) % 7) - 3) * 0.012;
    const jy = (((i * 17 + seed) % 5) - 2) * 0.014;
    return [(c + 0.5) / cols + jx, (r + 0.5) / rows + jy];
  });
}
